import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "react-hot-toast";

import { ErrorBoundary } from "@/components/shared/error-boundary";
import { brand } from "@/config/brand";
import { AuthProvider } from "@/features/auth/auth-context";
import "@/lib/i18n";
import "@/styles/globals.css";
import { routeTree } from "@/route-tree.gen";

if (import.meta.env.DEV && import.meta.env.VITE_FINDINGS_MOCK === "true") {
  const { installFindingsDevMock } =
    await import("@/features/findings/demo/dev-findings-mock");
  installFindingsDevMock();
}

document.title = `${brand.name} Console`;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root was not found.");
}

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
      <Toaster
        position="top-right"
        containerStyle={{ zIndex: 90000 }}
        reverseOrder={false}
      />
    </ErrorBoundary>
  </StrictMode>,
);
